import React from "react";

import { Users } from "../../../../../src/features/users/components/Users";

import { useParams } from "react-router-dom";
import { useAppSelector } from "../../../../../src/app/store";
import { selectEventByTypeAndEntityId } from "../../../../../src/features/timeline/logic/slice";
import { EventType } from "../../../../../src/api/events";
import { FinalFICDecision } from "../../../../../src/api/finalFIC";
import { useCommitteeMembers, useConveners } from "../../../users/logic/hooks";
import { formatDateForInput, getCurrentDate } from "../../../../lib/utils";
import ReportFormTable, { TextAreaInputCell, DateInputCell, RadioGroupInputCell, ReportFormRow, SelectInputCell, } from "../../components/common/ReportTableForm";


const fraudClassificationOptions: string[] = [
  'Misappropriation of funds and criminal breach of trust',
  'Fraudulent encashment through forged instruments',
  'Manipulation of books of accounts or through fictitious accounts, and conversion of property',
  'Cheating by concealment of facts with the intention to deceive any person and cheating by impersonation',
  'Forgery with the intention to commit fraud by making any false documents/electronic records',
  'Wilful falsification, destruction, alteration, mutilation of any book, electronic record, paper, writing, valuable security or account with intent to defraud',
  'Fraudulent credit facilities extended for illegal gratification',
  'Cash shortages on account of frauds',
  'Fraudulent transactions involving foreign exchange',
  'Fraudulent electronic banking / digital payment related transactions committed on NBFCs',
  'Other type of fraudulent activity not covered under any of the above.', 'No Fraud'
]

interface Props {
  committeeData: {
    convener: string;
    members: string[];
    date: string;
    decision?: FinalFICDecision;
    committeeName: string;
    fraudClassification: string;
  };

  onChange: (data: Partial<{
    convener: string;
    members: string[];
    date: string;
    decision?: FinalFICDecision;
    committeeName: string;
    fraudClassification: string;
  }>) => void;
}

export const FICDecisionCommitteeTable: React.FC<Props> = ({
  committeeData,
  onChange,
}) => {
  const { convener, members, decision, committeeName, fraudClassification } = committeeData;

  const { id: case_id } = useParams();

  const convenersList = useConveners();

  const membersList = useCommitteeMembers();

  const ficEvent = useAppSelector(
    case_id
      ? selectEventByTypeAndEntityId(EventType.FIC_FRAUD_CLASSIFICATION_COMPLETED, case_id)
      : () => undefined
  );

  const date = ficEvent?.date || committeeData.date;
  const isDateDisabled = !!ficEvent?.date;
  const isFraudClassificationDisabled =!decision || decision === FinalFICDecision.NO_FRAUD;

  const formRows: ReportFormRow[] = [
    {
      label: "Convener",
      inputCell: (
        <Users
          userIds={convener ? [convener] : []}
          maxVisible={1}
          addEnabled={!convener}
          deleteEnabled={!!convener}
          user_id_options={convenersList.map((u) => u.user_id)}
          onChange={(ids) =>
            onChange({ convener: ids[0] || "" })
          }
        />
      ),
    },
    {
      label: "Name of Committee",
      inputCell: (
        <TextAreaInputCell
          value={committeeName}
          onChange={(val) =>
            onChange({ committeeName: val })
          }
          rows={1}
          disableResize
          placeholder="Enter Name"
          textAreaClassName="h-[52px] pt-[14px] pb-[14px]"
        />
      ),
    },
    {
      label: "Members of Committee",
      inputCell: (
        <Users
          userIds={members}
          maxVisible={20}
          addEnabled={true}
          deleteEnabled={true}
          user_id_options={membersList.map((u) => u.user_id)}
          onChange={(ids) =>
            onChange({ members: ids })
          }
        />
      ),
    },
    {
      label: "Date of Committee",
      inputCell: (
        <DateInputCell
          value={date}
          onChange={(val) =>
            onChange({ date: val })
          }
          disabled={isDateDisabled}
          max={formatDateForInput(getCurrentDate())}
        />
      ),
    },
    {
      label: "Decision of Committee",
      inputCell: (
        <RadioGroupInputCell
          value={decision}
          onChange={(val) => 
            onChange({ decision: val as FinalFICDecision,
              fraudClassification:val === FinalFICDecision.NO_FRAUD ? "" : fraudClassification,
            })
          }
          options={[
            {
              label: "Fraud",
              value: FinalFICDecision.FRAUD,
              selectedBorderClass: "border-red-600",
              selectedBgClass: "bg-red-50",
              selectedDotClass: "bg-red-600",
            },
            {
              label: "No Fraud",
              value: FinalFICDecision.NO_FRAUD,
              selectedBorderClass: "border-green-600",
              selectedBgClass: "bg-green-50",
              selectedDotClass: "bg-green-600",
            },
            {
              label: "Attempted Fraud",
              value: FinalFICDecision.ATTEMPTED_FRAUD,
              selectedBorderClass: "border-yellow-600",
              selectedBgClass: "bg-yellow-50",
              selectedDotClass: "bg-yellow-600",
            },
          ]}
        />
      ),
    },
    {
      label: "Fraud Classification",
      disabled: isFraudClassificationDisabled,
      inputCell: (
        <SelectInputCell
          value={fraudClassification === 'NO_FRAUD' ? 'No Fraud' : fraudClassification}
          onChange={(value) =>
            onChange({fraudClassification:value === 'No Fraud' ? 'NO_FRAUD' : value})
          }
          options={fraudClassificationOptions.map((option) => ({
            name: option,
            value: option,
          }))}
          placeholder="nature of fraud"
          disabled={isFraudClassificationDisabled}
        />
      ),
    },
  ];

  return <ReportFormTable rows={formRows} />;
};